import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

export type PartnerOrder = {
  id: string
  status: string
  created_at: string
  client_name: string
  racket_brand: string
  total_price: number
  member_discount_amount: number
  partner_commission_amount: number
  coupon_code: string | null
}

export type OrderStatusFilter = 'ALL' | string
export type PeriodFilter = 'ALL' | '7d' | '30d' | '90d' | '12m'

// ── Helpers (exportés pour tests) ──────────────────────

export const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  EN_ATTENTE:        { label: 'En attente',        color: '#92400e', bg: '#fef3c7' },
  ACCEPTEE:          { label: 'Acceptée',          color: '#065f46', bg: '#d1fae5' },
  REFUSEE:           { label: 'Refusée',           color: '#991b1b', bg: '#fee2e2' },
  EN_COURS:          { label: 'En cours',          color: '#1e40af', bg: '#dbeafe' },
  PRET_A_RECUPERER:  { label: 'Prêt à récupérer',  color: '#6d28d9', bg: '#ede9fe' },
  EN_LIVRAISON:      { label: 'En livraison',      color: '#0369a1', bg: '#e0f2fe' },
  LIVREE:            { label: 'Livrée',            color: '#166534', bg: '#dcfce7' },
  CLOTUREE:          { label: 'Clôturée',          color: '#374151', bg: '#f3f4f6' },
  ANNULEE:           { label: 'Annulée',           color: '#991b1b', bg: '#fee2e2' },
}

export const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'ALL', label: 'Toutes les périodes' },
  { value: '7d',  label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: '90d', label: '3 derniers mois' },
  { value: '12m', label: '12 derniers mois' },
]

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

export const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount)

/** Calcule la date de début d'une période filtre */
export const getPeriodStartDate = (period: PeriodFilter): Date | null => {
  if (period === 'ALL') return null
  const now = new Date()
  switch (period) {
    case '7d':  return new Date(now.getTime() - 7  * 24 * 60 * 60 * 1000)
    case '30d': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    case '90d': return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
    case '12m': return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
    default:    return null
  }
}

/** Filtre les commandes côté client */
export const filterOrders = (
  orders: PartnerOrder[],
  statusFilter: OrderStatusFilter,
  periodFilter: PeriodFilter,
): PartnerOrder[] => {
  let result = orders
  if (statusFilter !== 'ALL') {
    result = result.filter((o) => o.status === statusFilter)
  }
  const startDate = getPeriodStartDate(periodFilter)
  if (startDate) {
    result = result.filter((o) => new Date(o.created_at) >= startDate)
  }
  return result
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  } as React.CSSProperties,
  select: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    background: 'white',
    color: '#334155',
  } as React.CSSProperties,
  filtersRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  } as React.CSSProperties,
  badge: (color: string, bg: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: bg,
    color,
  }),
  emptyState: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: '#94a3b8',
    fontSize: '0.95rem',
  } as React.CSSProperties,
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  summaryRow: {
    display: 'flex',
    gap: '1.5rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  summaryCard: (gradient: string): React.CSSProperties => ({
    flex: '1 1 200px',
    background: gradient,
    color: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }),
}

// ── Composant principal ────────────────────────────────

const PartnerOrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const { partnerProfile } = usePartnerAuth()
  const [orders, setOrders] = useState<PartnerOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('ALL')
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('ALL')

  const loadOrders = useCallback(async () => {
    if (!partnerProfile?.partner_id) return

    setLoading(true)
    setError(null)

    const { data, error: fetchError } = await supabase
      .from('orders')
      .select(`
        id,
        status,
        created_at,
        total_price,
        member_discount_amount,
        partner_commission_amount,
        racket_owner_name,
        racket_brand,
        coupon_id,
        coupon:coupons!coupon_id ( code ),
        profile:profiles!fk_orders_user ( full_name, email )
      `)
      .eq('partner_id', partnerProfile.partner_id)
      .order('created_at', { ascending: false })

    if (fetchError) {
      console.error('Error loading partner orders:', fetchError)
      setError('Erreur lors du chargement des commandes.')
      setOrders([])
    } else {
      const mapped: PartnerOrder[] = (data ?? []).map((o: any) => ({
        id: o.id,
        status: o.status,
        created_at: o.created_at,
        client_name: o.racket_owner_name || o.profile?.full_name || o.profile?.email || 'Client inconnu',
        racket_brand: o.racket_brand || '—',
        total_price: Number(o.total_price) || 0,
        member_discount_amount: Number(o.member_discount_amount) || 0,
        partner_commission_amount: Number(o.partner_commission_amount) || 0,
        coupon_code: o.coupon?.code ?? null,
      }))
      setOrders(mapped)
    }

    setLoading(false)
  }, [partnerProfile?.partner_id])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  const filtered = filterOrders(orders, statusFilter, periodFilter)

  // Statistiques résumées
  const totalCommission = filtered.reduce((s, o) => s + o.partner_commission_amount, 0)
  const totalDiscount = filtered.reduce((s, o) => s + o.member_discount_amount, 0)

  // Liste unique des statuts présents
  const availableStatuses = [...new Set(orders.map((o) => o.status))]

  return (
    <div data-testid="orders-page">
      <h2
        style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          marginTop: 0,
          marginBottom: '1.5rem',
          color: '#1e293b',
        }}
      >
        Commandes partenaire
      </h2>

      {error && (
        <div style={styles.errorBox} data-testid="orders-error">
          {error}
        </div>
      )}

      {/* Résumé */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard('linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)')}>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.85rem' }}>Commandes affichées</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: '600' }} data-testid="orders-count">
            {filtered.length}
          </p>
        </div>
        <div style={styles.summaryCard('linear-gradient(135deg, #10b981 0%, #047857 100%)')}>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.85rem' }}>Commission totale</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: '600' }} data-testid="total-commission">
            {formatCurrency(totalCommission)}
          </p>
        </div>
        <div style={styles.summaryCard('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)')}>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.85rem' }}>Réduction membres</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: '600' }} data-testid="total-discount">
            {formatCurrency(totalDiscount)}
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div style={styles.card}>
        <div style={styles.filtersRow}>
          <div>
            <label
              htmlFor="status-filter"
              style={{ fontSize: '0.85rem', color: '#64748b', marginRight: '0.5rem' }}
            >
              Statut :
            </label>
            <select
              id="status-filter"
              data-testid="status-filter"
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">Tous les statuts</option>
              {availableStatuses.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]?.label ?? s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="period-filter"
              style={{ fontSize: '0.85rem', color: '#64748b', marginRight: '0.5rem' }}
            >
              Période :
            </label>
            <select
              id="period-filter"
              data-testid="period-filter"
              style={styles.select}
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value as PeriodFilter)}
            >
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tableau */}
        {loading ? (
          <div style={styles.emptyState} data-testid="orders-loading">
            Chargement des commandes...
          </div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState} data-testid="orders-empty">
            Aucune commande trouvée pour les filtres sélectionnés.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} data-testid="orders-table">
              <thead>
                <tr>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Propriétaire</th>
                  <th style={styles.th}>Raquette</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Réduction</th>
                  <th style={styles.th}>Commission</th>
                  <th style={styles.th}>Coupon</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const statusInfo = STATUS_LABELS[order.status] ?? {
                    label: order.status,
                    color: '#475569',
                    bg: '#f1f5f9',
                  }
                  return (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td style={styles.td}>
                        <span style={styles.badge(statusInfo.color, statusInfo.bg)}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDate(order.created_at)}</td>
                      <td style={styles.td}>{order.client_name}</td>
                      <td style={styles.td}>{order.racket_brand}</td>
                      <td style={styles.td}>{formatCurrency(order.total_price)}</td>
                      <td style={styles.td}>{formatCurrency(order.member_discount_amount)}</td>
                      <td style={styles.td}>{formatCurrency(order.partner_commission_amount)}</td>
                      <td style={styles.td}>
                        {order.coupon_code ? (
                          <span
                            style={{
                              background: '#ede9fe',
                              color: '#6d28d9',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.85rem',
                              fontWeight: '500',
                            }}
                          >
                            {order.coupon_code}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => navigate(`/order/confirmation?orderId=${order.id}`)}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            padding: '0.3rem 0.75rem',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                          }}
                          data-testid={`order-detail-${order.id}`}
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default PartnerOrdersPage
